import { OrderResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import React from 'react';
import Divider from '../components/Divider';
import LineItem from '../components/LineItem';
import PurchaseSummary from '../components/PurchaseSummary';
import {
    OrderViewLineItems,
    OrderViewPrimarySectionTitle,
    OrderViewSection,
    OrderViewSectionTitle,
} from '../styles';
import { useEcommStr } from '../useEcommStr';
import { readOrderIdFromUrl } from '../useOrder';

interface LineItemsSectionProps {
    order: OrderResponseDto;
    // When the intro header is shown it already surfaces the invoice number, so
    // the summary title drops the ` - <invoice>` suffix and reads just "Order
    // Summary". When the header is hidden the suffix stays (legacy behavior).
    showIntro?: boolean;
}

const LineItemsSection: React.FC<LineItemsSectionProps> = ({
    order,
    showIntro = false,
}) => {
    const str = useEcommStr();
    // Same precedence as the cancellation request: the URL carries the id the
    // order was fetched with, and the payload id is the fallback.
    const orderId = readOrderIdFromUrl() ?? order.id;
    const title = (
        <>
            {str('nativeEcommRuntime.order.summary')}
            {!showIntro && order.invoice_number
                ? ` - ${order.invoice_number}`
                : ''}
        </>
    );

    return (
        <OrderViewSection>
            {/* Without the intro header this is the widget's top-level title, so
                it takes the "Primary title" design settings; with the intro it
                is one details title among the others. */}
            {showIntro ? (
                <OrderViewSectionTitle>{title}</OrderViewSectionTitle>
            ) : (
                <OrderViewPrimarySectionTitle>
                    {title}
                </OrderViewPrimarySectionTitle>
            )}
            <OrderViewLineItems>
                {order.items.map((item, index) => (
                    <React.Fragment key={item.id}>
                        <LineItem
                            item={item}
                            currency={order.currency}
                            orderId={orderId}
                            canDownloadDigitalResources={
                                order.can_download_digital_resources
                            }
                        />
                        {index < order.items.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </OrderViewLineItems>
            <PurchaseSummary
                summary={order.summary}
                currency={order.currency}
                hasTaxesIncludedInPrices={order.has_taxes_included_in_prices}
            />
        </OrderViewSection>
    );
};

export default LineItemsSection;
